import type { CustomValidityFunction } from "@aerstudios/aerated-aerinputstructure/AerInputStructure";
import type React from "react";
// LOOKING FOR regexp PATTERNS? see stringUtils

export const tooShort = (validity?: ValidityState, min?: number): boolean => {
	if (
		validity?.tooShort ||
		(validity?.valueMissing && min) ||
		validity?.rangeUnderflow
	) {
		return true;
	}

	return false;
};
export const tooLong = (validity?: ValidityState): boolean => {
	if (validity?.rangeOverflow || validity?.tooLong) {
		return true;
	}

	return false;
};

export const hasBadInput = (validity?: ValidityState): boolean => {
	if (
		validity?.patternMismatch ||
		validity?.badInput ||
		validity?.typeMismatch ||
		validity?.stepMismatch
	) {
		return true;
	}

	return false;
};

const getCharacterPlural = (count: number) =>
	count === 1 ? "character" : "characters";

// A permissive type that covers all the HTML attributes that can be used for validation
export interface ValidityAttributes extends HTMLElement {
	minlength?: number;
	maxlength?: number;
	value?: string;
	text?: string;
	pattern?: string;
	required?: boolean;
	checked?: boolean;
}

/**
 * Behaviour is to only return the validity message if it is badInput or `showAll` is passed
 */
export const handleValidityMessage = (
	element?: ValidityAttributes | null,
	validity?: ValidityState,
	showAll = false,
): string => {
	if (!validity || validity.valid || !element) {
		return "";
	}

	const type = element.getAttribute("type");
	const min = element.getAttribute("minlength") ?? "0";
	const max = element.getAttribute("maxlength");

	if (showAll || hasBadInput(validity)) {
		let badInputMessage = "The text entered should only use valid characters";
		switch (type) {
			case "email":
				badInputMessage = "Invalid email address";
				break;
		}

		return hasBadInput(validity)
			? badInputMessage
			: validity.valueMissing
				? "This field must be completed"
				: tooShort(validity, Number.parseInt(min, 10))
					? `The text entered should be at least ${min} ${getCharacterPlural(
							Number.parseInt(min, 10),
						)} long`
					: tooLong(validity)
						? // if there is a tooLong validity, then there must be a max
							max
							? `The text entered should be no more than ${max} ${getCharacterPlural(
									Number.parseInt(max, 10),
								)} long`
							: ""
						: "";
	}

	return "";
};

export const validValidityState: ValidityState = {
	valid: true,
	badInput: false,
	customError: false,
	patternMismatch: false,
	rangeOverflow: false,
	rangeUnderflow: false,
	stepMismatch: false,
	tooLong: false,
	tooShort: false,
	typeMismatch: false,
	valueMissing: false,
};

export const checkTextAreaValidity: CustomValidityFunction = (
	textAreaElement: ValidityAttributes | null,
): ValidityState => {
	let validity: ValidityState = {
		...validValidityState,
	};

	if (!textAreaElement) {
		return validity;
	}

	const min = textAreaElement.minlength;
	const max = textAreaElement.getAttribute("maxlength");
	const text = textAreaElement.value;
	const pattern = textAreaElement.getAttribute("pattern");

	// use the pattern as a regular expression to test the text for the presence of unexpected characters
	const matched = pattern ? text?.match(new RegExp(pattern, "ugim")) : null;

	if (typeof text === "string") {
		if (pattern && text.length) {
			if (!matched || matched[0] !== text) {
				validity = {
					...validity,
					...{ patternMismatch: true, valid: false },
				};
			}
		}

		if (min && text.length < min) {
			validity = { ...validity, ...{ tooShort: true, valid: false } };
		}

		if (max && text.length > Number.parseInt(max, 10)) {
			validity = { ...validity, ...{ tooLong: true, valid: false } };
		}
	} else if (min) {
		validity = { ...validity, ...{ tooShort: true, valid: false } };
	}

	return validity;
};

export const checkCheckableValidity: CustomValidityFunction = (
	checkableElement: ValidityAttributes | null,
) => {
	let validity: ValidityState = {
		...validValidityState,
	};

	if (!checkableElement) {
		return validity;
	}

	const isRequired =
		checkableElement.required ||
		checkableElement.getAttribute("aria-required") === "true";
	const isChecked =
		checkableElement.checked ||
		checkableElement.getAttribute("aria-checked") === "true";

	if (isRequired && !isChecked) {
		validity = { ...validity, ...{ valueMissing: true, valid: false } };
	}

	return validity;
};

export const focusAndReturnADescendant = (
	element: HTMLElement,
	currentFocusIndex: number,
	menu: Array<string | React.ReactElement | null> = [],
): Element | null => {
	const activeElement = document?.activeElement;
	let newActive: Element | null;

	const focusADescendant = (
		element: HTMLElement,
		currentFocusIndex: number,
		menu: Array<string | React.ReactElement | null> = [],
	): Element | null => {
		if (!element && currentFocusIndex >= menu.length) {
			return null;
		}

		const { children } = element;

		for (let i = 0; i < children.length; i++) {
			// Loop through and try to focus all child elements
			(children[i] as HTMLElement).focus();

			// If the focus was successful i.e. the
			// active element has changed, stop.
			newActive = document?.activeElement;
			if (newActive !== activeElement) {
				break;
			}
		}

		if (newActive === activeElement) {
			// Repeat the process and loop through children of the activeElement
			for (let i = 0; i < children.length; i++) {
				focusADescendant(children[i] as HTMLElement, currentFocusIndex, menu);

				// Stop if the focus was successful.
				newActive = document.activeElement;
				if (newActive !== activeElement) {
					break;
				}
			}
		}

		return activeElement;
	};

	return focusADescendant(element, currentFocusIndex, menu);
};
